/**
 * Contains generic utility methods.
 *
 * ```js
 * // esm
 * import { utils } from '@paychex/state';
 *
 * // cjs
 * const { utils } = require('@paychex/state');
 *
 * // iife
 * const { utils } = window['@paychex/state'];
 *
 * // amd
 * require(['@paychex/state'], function({ utils }) { ... });
 * define(['@paychex/state'], function({ utils }) { ... });
 * ```
 *
 * @module utils
 */

import { errors } from '@paychex/core';
import { curry } from 'lodash';

import {
    satisfies as sat,
    valid,
    validRange,
    gtr,
} from 'semver';

/**
 * Provides load, save, and display formatters.
 */
export interface FlexFormatter {

    /**
     * Converts the values into a format for saving.
     *
     * @param values The values to format for saving.
     * @returns A value formatted for saving.
     */
    save(values: string[]): any

    /**
     * Converts the values into a format for display.
     *
     * @param values The values to format for display.
     * @returns A string formatted for display.
     */
    display(values: string[]): string

    /**
     * Converts the values into a format for binding to a form input's value.
     *
     * @param values The values to format for binding to an input.
     * @returns A string formatted for assignment to a form input's value property.
     */
    load(values: string[]): string

}

function validate(version: string, range: string): [string, string] {
    const v = valid(version);
    const p = validRange(range);
    if (!v) throw errors.error('Semantic version is invalid.', { version });
    if (!p) throw errors.error('Version range is invalid.', { version });
    return [v, p];
}

/**
 * Determines if the specified pattern/range is satisfied by the given version.
 *
 * **NOTE:** This function is curried, so you can partially apply the semantic
 * version ahead of time. See the examples for details.
 *
 * @param version The {@link https://semver.org/ semantic version} to check the pattern against.
 * @param pattern The {@link https://docs.npmjs.com/cli/v6/using-npm/semver version pattern} to check against.
 * @throws Semantic version is invalid.
 * @throws Version range is invalid.
 * @returns `true`, if the version satisfies the pattern/range; otherwise, `false`
 * @example
 * ```js
 * // satisfying versions:
 * utils.satisfies('1.2.3', 'v1'); // true
 * utils.satisfies('1.2.3', '1.x.x'); // true
 * utils.satisfies('1.2.3', '>=1.2.3'); // true
 * utils.satisfies('1.2.3', '1.2 - 1.4'); // true
 *
 * // non-satisfying versions:
 * utils.satisfies('1.2.3', 'v2'); // false
 * utils.satisfies('1.2.3', '1.2.4 || >= 2.x.x'); // false
 * utils.satisfies('1.2.3', '^1.x.x'); // false
 * ```
 * @example
 * ```js
 * // currying
 * const isOkayWithV1 = utils.satisfies('1.0.0');
 *
 * isOkayWithV1('*'); // true
 * isOkayWithV1('v1'); // true
 * isOkayWithV1('1.x.x'); // true
 * isOkayWithV1('>1 <2'); // true
 * ```
 */
export const satisfies = curry(function satisfies(version: string, pattern: string): boolean {
    const [v, p] = validate(version, pattern);
    return sat(v, p);
}, 2);

/**
 * Determines if the specified version is greater than or equal to the given pattern/range.
 *
 * **NOTE:** This function is curried, so you can partially apply the semantic
 * version ahead of time. See the examples for details.
 *
 * @param version The {@link https://semver.org/ semantic version} to check the pattern against.
 * @param pattern The {@link https://docs.npmjs.com/cli/v6/using-npm/semver version pattern} to check against.
 * @throws Semantic version is invalid.
 * @throws Version range is invalid.
 * @returns `true`, if the version is greater than or equal to the pattern/range; otherwise, `false`
 * @example
 * ```js
 * // satisfying versions:
 * utils.newer('1.2.3', 'v1'); // true
 * utils.newer('1.2.3', '1.x.x'); // true
 * utils.newer('1.2.3', '1.2.3'); // true
 *
 * // non-satisfying versions:
 * utils.newer('1.2.3', 'v2'); // false
 * utils.newer('1.2.3', '1.2.4'); // false
 * ```
 * @example
 * ```js
 * // currying
 * const isNewerThanV1 = utils.newer('1.0.0');
 *
 * isNewerThanV1('v1'); // true
 * isNewerThanV1('1.x.x'); // true
 * isNewerThanV1('>1 <2'); // true
 * isNewerThanV1('2.x.x'); // false
 * ```
 */
export const newer = curry(function newer(version: string, pattern: string): boolean {
    const [v, p] = validate(version, pattern);
    return gtr(v, p) || sat(v, p);
}, 2);
